import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { promoAPI } from '../../services/api';

const BADGE_OPTIONS = [
  { value: 'exclusive', label: 'حصري',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'limited',   label: 'محدود',  cls: 'bg-red-50 text-red-600 border-red-200' },
  { value: 'permanent', label: 'دائم',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const badgeLabel = (v) => BADGE_OPTIONS.find((b) => b.value === v)?.label || v;
const badgeCls   = (v) => BADGE_OPTIONS.find((b) => b.value === v)?.cls || '';

const EMPTY = { title: '', code: '', badge: 'exclusive', discountPercent: 0, validUntil: '' };

const PromosPage = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(EMPTY);
  const [adding, setAdding]   = useState(false);
  const [editId, setEditId]   = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    promoAPI.getMine()
      .then((res) => setRows(res.data.data || []))
      .catch(() => toast.error('فشل تحميل العروض'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.code.trim()) return;
    setAdding(true);
    try {
      await promoAPI.create({ ...form, validUntil: form.validUntil || null });
      toast.success('تم إضافة العرض');
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإضافة');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditData({
      title: row.title, code: row.code, badge: row.badge,
      discountPercent: row.discountPercent ?? 0,
      validUntil: row.validUntil || '', isEnabled: row.isEnabled,
    });
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await promoAPI.update(id, { ...editData, validUntil: editData.validUntil || null });
      toast.success('تم الحفظ');
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    try {
      await promoAPI.update(row.id, { isEnabled: !row.isEnabled });
      load();
    } catch {
      toast.error('فشل التحديث');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`حذف عرض "${row.title}"؟`)) return;
    try {
      await promoAPI.remove(row.id);
      toast.success('تم الحذف');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحذف');
    }
  };

  const f  = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const ef = (key) => (e) => setEditData((p) => ({ ...p, [key]: e.target.value }));

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">

      <div>
        <h1 className="text-2xl font-bold text-gray-800">🎁 عروض الخصم</h1>
        <p className="text-sm text-gray-500 mt-1">
          أنشئ كوبونات خصم لشركتك — تظهر للمسافرين المسجّلين في الصفحة الرئيسية.
        </p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="bg-white rounded-2xl shadow p-5 space-y-4">
        <h2 className="font-semibold text-gray-700 text-sm">إضافة عرض جديد</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">عنوان العرض</label>
            <input value={form.title} onChange={f('title')} required
              placeholder="مثال: خصم 10% على رحلات نهاية الأسبوع"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">كود الخصم</label>
            <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
              required placeholder="SAVE10"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">نوع الشارة</label>
            <select value={form.badge} onChange={f('badge')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {BADGE_OPTIONS.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">نسبة الخصم (%)</label>
            <div className="relative">
              <input type="number" min="0" max="100" value={form.discountPercent}
                onChange={(e) => setForm((p) => ({ ...p, discountPercent: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8" />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">تاريخ الانتهاء (اختياري)</label>
            <input type="date" value={form.validUntil} onChange={f('validUntil')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
        <button type="submit" disabled={adding || !form.title.trim() || !form.code.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl px-6 py-2 text-sm transition">
          {adding ? '...' : '+ إضافة عرض'}
        </button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="px-5 py-3 border-b">
          <span className="font-semibold text-gray-700 text-sm">عروضك ({rows.length})</span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">جاري التحميل...</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">لا توجد عروض بعد — أضف أول عرض أعلاه.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-right font-medium">العنوان</th>
                  <th className="px-4 py-3 text-right font-medium">الكود</th>
                  <th className="px-4 py-3 text-right font-medium">الخصم</th>
                  <th className="px-4 py-3 text-right font-medium">النوع</th>
                  <th className="px-4 py-3 text-right font-medium">تاريخ الانتهاء</th>
                  <th className="px-4 py-3 text-right font-medium">الحالة</th>
                  <th className="px-4 py-3 text-left  font-medium">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row) => (
                  <tr key={row.id} className={`hover:bg-gray-50/60 ${!row.isEnabled ? 'opacity-50' : ''}`}>
                    {editId === row.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input value={editData.title} onChange={ef('title')}
                            className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-full focus:outline-none" />
                        </td>
                        <td className="px-4 py-2">
                          <input value={editData.code}
                            onChange={(e) => setEditData((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                            className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-28 font-mono uppercase focus:outline-none" />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <input type="number" min="0" max="100"
                              value={editData.discountPercent ?? 0}
                              onChange={(e) => setEditData((p) => ({ ...p, discountPercent: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }))}
                              className="border border-blue-300 rounded-lg px-2 py-1 text-xs w-16 focus:outline-none" />
                            <span className="text-xs text-gray-500">%</span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <select value={editData.badge} onChange={ef('badge')}
                            className="border border-blue-300 rounded-lg px-2 py-1 text-xs focus:outline-none">
                            {BADGE_OPTIONS.map((b) => (
                              <option key={b.value} value={b.value}>{b.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          <input type="date" value={editData.validUntil} onChange={ef('validUntil')}
                            className="border border-blue-300 rounded-lg px-2 py-1 text-xs focus:outline-none" />
                        </td>
                        <td className="px-4 py-2">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={editData.isEnabled}
                              onChange={(e) => setEditData((p) => ({ ...p, isEnabled: e.target.checked }))} />
                            <span className="text-xs">{editData.isEnabled ? 'مفعّل' : 'معطّل'}</span>
                          </label>
                        </td>
                        <td className="px-4 py-2 text-left">
                          <button onClick={() => handleSave(row.id)} disabled={saving}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg ml-1 transition">
                            {saving ? '...' : 'حفظ'}
                          </button>
                          <button onClick={() => setEditId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 transition">
                            إلغاء
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-semibold text-gray-800 max-w-[200px] truncate">{row.title}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono font-bold text-[#1E3A8A] tracking-widest text-xs bg-[#EEF2FF] px-2 py-1 rounded-lg" dir="ltr">
                            {row.code}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${(row.discountPercent || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            {row.discountPercent || 0}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full border ${badgeCls(row.badge)}`}>
                            {badgeLabel(row.badge)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {row.validUntil ? row.validUntil : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleToggle(row)}
                            className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                              row.isEnabled
                                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                            }`}>
                            {row.isEnabled ? 'مفعّل' : 'معطّل'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-left">
                          <button onClick={() => startEdit(row)}
                            className="text-xs text-blue-600 hover:text-blue-800 ml-3 transition">
                            تعديل
                          </button>
                          <button onClick={() => handleDelete(row)}
                            className="text-xs text-red-500 hover:text-red-700 transition">
                            حذف
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromosPage;
